import { Download, FileText, Headphones, Loader2 } from "lucide-react";
import clsx from "clsx";
import { fileSize, type Demo } from "@/api/demos";
import { useDemoUrl } from "@/lib/useDemoUrl";

function extension(name: string): string {
  return name.includes(".") ? name.split(".").pop()!.toUpperCase().slice(0, 5) : "FILE";
}

/** The full player/viewer for one demo. */
export default function DemoMedia({ demo, className }: { demo: Demo; className?: string }) {
  const url = useDemoUrl(demo.id).data;
  const download = url ? `${url}&download=1` : undefined;

  if (!url) {
    return (
      <div
        className={clsx("grid aspect-video place-items-center rounded-xl bg-ink-800", className)}
      >
        <Loader2 className="h-5 w-5 animate-spin text-mut-500" />
      </div>
    );
  }
  switch (demo.kind) {
    case "video":
      return (
        <video
          key={url}
          src={url}
          controls
          playsInline
          preload="metadata"
          className={clsx("w-full rounded-xl bg-black", className)}
        />
      );
    case "audio":
      return (
        <div className={clsx("rounded-xl bg-ink-800 p-6", className)}>
          <div className="mb-4 flex items-center gap-3">
            <span
              className="grid h-12 w-12 place-items-center rounded-xl text-white"
              style={{ background: "var(--grad)" }}
            >
              <Headphones className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium">{demo.filename}</div>
              <div className="text-sm text-mut-500">{fileSize(demo.size_bytes)}</div>
            </div>
          </div>
          <audio key={url} src={url} controls preload="metadata" className="w-full" />
        </div>
      );
    case "image":
      return (
        <img
          src={url}
          alt={demo.title}
          className={clsx("w-full rounded-xl bg-ink-800 object-contain", className)}
        />
      );
    case "pdf":
      return (
        <iframe
          src={url}
          title={demo.title}
          className={clsx("h-[70vh] w-full rounded-xl border border-fg/[0.08] bg-white", className)}
        />
      );
    default:
      return (
        <div
          className={clsx(
            "grid place-items-center gap-3 rounded-xl bg-ink-800 p-10 text-center",
            className,
          )}
        >
          <span className="grid h-16 w-16 place-items-center rounded-2xl bg-ink-900 text-mut-300">
            <FileText className="h-7 w-7" />
          </span>
          <div>
            <div className="font-medium">{demo.filename}</div>
            <div className="text-sm text-mut-500">
              {extension(demo.filename)} · {fileSize(demo.size_bytes)} · opens in its own app
            </div>
          </div>
          <a className="btn btn-primary" href={download}>
            <Download className="h-4 w-4" /> Download
          </a>
        </div>
      );
  }
}

/** A small preview for cards: a real video frame, the image, or a typed tile. */
export function DemoThumb({ demo }: { demo: Demo }) {
  const wantsUrl = demo.kind === "video" || demo.kind === "image";
  const url = useDemoUrl(demo.id, wantsUrl).data;
  if (demo.kind === "video" && url) {
    return (
      <video src={`${url}#t=1`} muted preload="metadata" className="h-full w-full object-cover" />
    );
  }
  if (demo.kind === "image" && url) {
    return <img src={url} alt="" className="h-full w-full object-cover" />;
  }
  const tone =
    demo.kind === "audio"
      ? "var(--grad)"
      : demo.kind === "pdf"
        ? "rgb(var(--pink))"
        : "rgb(var(--cyan))";
  return (
    <div className="grid h-full w-full place-items-center text-white" style={{ background: tone }}>
      {demo.kind === "audio" ? (
        <Headphones className="h-8 w-8 opacity-90" />
      ) : (
        <span className="font-mono text-lg font-semibold opacity-90">
          {extension(demo.filename)}
        </span>
      )}
    </div>
  );
}
